<script setup>
import { ref, computed } from 'vue';
import { useAccount } from 'dashboard/composables/useAccount';

const { accountId } = useAccount();

const isConnecting = ref(false);
const hasError = ref(false);
const errorMessage = ref('');

const postizUrl = computed(() => {
  return window.chatwootConfig?.postizUrl || 'http://localhost:3700';
});

const connectGoogleBusiness = async () => {
  try {
    isConnecting.value = true;

    // Call Postiz API to get the Google Business OAuth URL
    const response = await fetch(`${postizUrl.value}/integrations/social/gmb`);
    const { url, err } = await response.json();

    if (err) {
      hasError.value = true;
      errorMessage.value = 'Could not connect to Google Business';
      isConnecting.value = false;
      return;
    }

    // Redirect to Google Business OAuth via Postiz
    window.location.href = url;
  } catch (error) {
    hasError.value = true;
    errorMessage.value = error.message || 'Failed to connect to Google Business';
    isConnecting.value = false;
  }
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full">
      <!-- Error State -->
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5 class="text-xl font-semibold text-red-600 mb-2">Connection Error</h5>
        <p class="text-sm text-n-slate-11">{{ errorMessage }}</p>
      </div>

      <!-- Google Business Connection -->
      <div v-else class="w-full max-w-2xl">
        <div class="text-center mb-8">
          <h6 class="text-2xl font-medium mb-2">Connect Google Business Profile</h6>
          <p class="text-sm text-n-slate-11">
            Connect your Google My Business profile to manage customer messages and reviews
          </p>
        </div>

        <div class="flex justify-center">
          <div
            class="relative group cursor-pointer w-full max-w-md"
            @click="connectGoogleBusiness"
          >
            <div
              class="h-full p-8 rounded-2xl border-2 transition-all duration-300"
              :class="[
                isConnecting
                  ? 'border-blue-600 bg-blue-50'
                  : 'border-n-weak hover:border-blue-500 hover:shadow-lg'
              ]"
            >
              <!-- Google Business Icon -->
              <div
                class="w-20 h-20 mb-6 mx-auto rounded-full flex items-center justify-center bg-gradient-to-br from-blue-600 via-red-600 to-yellow-500 text-white"
              >
                <span class="i-ri-google-fill text-5xl" />
              </div>

              <!-- Title & Description -->
              <h6 class="text-lg font-semibold mb-2 text-center">Google Business Profile</h6>
              <p class="text-sm text-n-slate-11 mb-6 text-center">
                Connect your Google My Business profile to manage customer messages, respond to reviews, and engage with people discovering your business on Google Search and Maps
              </p>

              <!-- Connect Button -->
              <button
                class="w-full flex items-center justify-center px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isConnecting"
                @click.stop="connectGoogleBusiness"
              >
                <span v-if="isConnecting" class="mr-2">
                  <svg
                    class="w-5 h-5 animate-spin"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    />
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </span>
                <span class="i-ri-google-fill mr-2 text-xl" />
                <span class="font-medium">
                  {{ isConnecting ? 'Connecting...' : 'Connect Google Business Profile' }}
                </span>
              </button>
            </div>
          </div>
        </div>

        <!-- Help Text -->
        <div class="mt-8 p-4 bg-blue-50 rounded-lg">
          <h6 class="text-sm font-medium text-blue-900 mb-2">
            What happens next?
          </h6>
          <ol class="text-xs text-blue-800 space-y-1 list-decimal list-inside">
            <li>You'll be redirected to Google to authorize the connection</li>
            <li>Select the business profile you want to connect</li>
            <li>Grant the necessary permissions to manage messages and reviews</li>
            <li>You'll be redirected back to complete the channel setup</li>
            <li>Start managing customer interactions from your inbox</li>
          </ol>
        </div>

        <!-- Features -->
        <div class="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div class="p-4 bg-white border border-n-weak rounded-lg">
            <div class="w-10 h-10 mb-2 rounded-full bg-blue-100 flex items-center justify-center">
              <span class="i-ri-customer-service-2-line text-blue-600 text-xl" />
            </div>
            <h6 class="text-sm font-medium mb-1">Message Management</h6>
            <p class="text-xs text-n-slate-11">Respond to customer messages from Google</p>
          </div>
          <div class="p-4 bg-white border border-n-weak rounded-lg">
            <div class="w-10 h-10 mb-2 rounded-full bg-red-100 flex items-center justify-center">
              <span class="i-ri-star-line text-red-600 text-xl" />
            </div>
            <h6 class="text-sm font-medium mb-1">Review Management</h6>
            <p class="text-xs text-n-slate-11">Monitor and respond to customer reviews</p>
          </div>
          <div class="p-4 bg-white border border-n-weak rounded-lg">
            <div class="w-10 h-10 mb-2 rounded-full bg-yellow-100 flex items-center justify-center">
              <span class="i-ri-map-pin-line text-yellow-600 text-xl" />
            </div>
            <h6 class="text-sm font-medium mb-1">Local Engagement</h6>
            <p class="text-xs text-n-slate-11">Connect with local customers on Search & Maps</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
